// The screens this application has, and the path each one is at.
//
// Pure: no React, no browser, no I/O, so the vocabulary can be read and tested
// on its own - the same split `day/day-model.ts` and `edit/edit-model.ts` keep.
//
// THIS FILE IS THE ONE PLACE A SCREEN IS NAMED. Adding one is adding a line to
// `SCREENS` below and a case in `App.tsx`; nothing else in the client writes a
// screen name or a path down. That is the whole point of the shell: screens may
// be built independently, but separate answers to "which screen am I on" would
// be incompatible applications.

/**
 * Every screen. All of them are built now: `today`, `month`, `reminders`,
 * `notes`, `realize`, `progress` and `board`, with `settings` as the shell's
 * own.
 */
export type ScreenName =
  'today' | 'month' | 'notes' | 'realize' | 'progress' | 'reminders' | 'settings' | 'board';

export interface Screen {
  key: ScreenName;
  /** The path the browser shows for it. `today` is the root. */
  path: string;
  /** Its name in the navigation, and the heading a placeholder writes. */
  label: string;
  /** A name in `day/Icon.tsx`. */
  icon: string;
  /**
   * Whether it is one of the four the tab bar and the sidebar list.
   *
   * Settings is deliberately not: the approved design reaches it from the
   * account button on a phone and from the foot of the sidebar on a desktop
   * (prototype-v1.md section 6, `screenshots/phone-settings.png`), which keeps
   * the daily path short. A screen that is not a tab is the shape Month and
   * Reminders take too - both are opened from the day rather than navigated to -
   * so the shell has to hold both kinds, and this is where that distinction
   * lives rather than in whoever builds the next one.
   */
  tab: boolean;
}

export const SCREENS: readonly Screen[] = [
  { key: 'today', path: '/', label: 'Today', icon: 'today', tab: true },
  // NOT A TAB, by the same captain decision that made the Board one (2026-08-18,
  // below): "Month and reminders should be in the main tab which is the Today
  // tab." It takes the shape Settings takes instead - a screen opened from
  // somewhere, with a header carrying its way out. Its entry point is the
  // control in the day screen's date header, at every width, because the month
  // is the day's own question over a longer span - `DayScreen.tsx` says the rest
  // of that at the control itself.
  { key: 'month', path: '/month', label: 'Month', icon: 'month', tab: false },
  { key: 'notes', path: '/notes', label: 'Notes', icon: 'note', tab: true },
  // NOT A TAB, and the third screen that is not. It is a page INSIDE the Notes
  // tab (captain, 2026-08-19: "I also want notes tab to have another page inside
  // which is called Realize"), reached from the row at the top of the notes
  // list - the same shape Month and Reminders take off the Today tab.
  //
  // THE NAVIGATION IS STILL FOUR TABS. AGENTS.md names "a fifth tab" as the
  // likely future accident, and a mind mapper is exactly the kind of thing that
  // would ask for one: it is a place you go and stay in. It is not a place you
  // plan your day from, which is what the four are for.
  { key: 'realize', path: '/realize', label: 'Realize', icon: 'realize', tab: false },
  { key: 'progress', path: '/progress', label: 'Progress', icon: 'grid', tab: true },
  // Not a tab, and the second screen that is not: the approved design opens it
  // from the reminders block on the day, exactly as Settings is opened from the
  // account button (prototype-v2.md section 1.2). A tab is a place you have to
  // remember to visit, and a reminder is by definition the thing that gets
  // forgotten - so filing it behind a tab is the mechanism by which it is.
  { key: 'reminders', path: '/reminders', label: 'Reminders', icon: 'pin', tab: false },
  { key: 'settings', path: '/settings', label: 'Settings', icon: 'gear', tab: false },
  // A TAB, by captain decision (2026-08-18): "can you make it it's own section.
  // It should not be in the settings." It shipped in v0.4.0 as a row at the top
  // of Settings; this is the captain settling it.
  //
  // THE NAVIGATION IS THESE FOUR - Today, Notes, Progress, Board - with Settings
  // beside them, and the same decision settled the other two: Month and
  // Reminders are reached from the Today tab, where they already are. Do not
  // move them here, and do not add a fifth. AGENTS.md names "a fifth tab or a
  // Board row restored to Settings" as the likely future accident.
  { key: 'board', path: '/board', label: 'Board', icon: 'board', tab: true },
];

export const TABS: readonly Screen[] = SCREENS.filter((screen) => screen.tab);

/** Where the shell starts, and where an unreadable path is corrected to. */
export const HOME: ScreenName = 'today';

export function screenFor(key: ScreenName): Screen {
  // `SCREENS` covers the union, so this cannot miss; the fallback exists
  // because the type system cannot prove that of an array.
  return SCREENS.find((screen) => screen.key === key) ?? SCREENS[0]!;
}

export function pathOf(key: ScreenName): string {
  return screenFor(key).path;
}

/**
 * Which screen a path names, or null when none of them does.
 *
 * Null is the case worth having: the server hands `index.html` back for ANY
 * unknown non-API path (`api/src/http/error-handler.ts`), so a typo, an old
 * link or a path belonging to a screen that has not been built yet all arrive
 * here rather than at a 404. Answering "today" silently would leave the address
 * bar naming a screen the person is not looking at; the caller corrects the URL
 * instead, which is why this reports rather than defaults.
 */
export function screenAt(path: string): ScreenName | null {
  // '/notes/' and '/notes' are the same screen; '/' is not shortened to ''.
  const trimmed = path.length > 1 && path.endsWith('/') ? path.slice(0, -1) : path;
  return SCREENS.find((screen) => screen.path === trimmed)?.key ?? null;
}

/**
 * What a screen that is not built yet says for itself.
 *
 * A blank panel behind a tab reads as a broken application, and the first thing
 * anybody does with one is press it again. Each of these says what the screen
 * IS, in the words the product already uses for it, and then says plainly that
 * it is not here yet - so the tab is an honest promise rather than a dead end.
 *
 * The workstream that builds one deletes its entry here and mounts its screen in
 * `App.tsx`. Nothing else has to change.
 */
export interface Unbuilt {
  heading: string;
  body: string;
}

export const UNBUILT: Readonly<Partial<Record<ScreenName, Unbuilt>>> = {
  // EMPTY, and that is the finished state rather than a gap: every screen in
  // `SCREENS` above is built. `Placeholder` still exists and still reads from
  // here, so the next screen added before its own workstream finishes is one
  // entry away from an honest tab - which is why neither this record nor the
  // component was deleted with the last placeholder in it.
  //
  // The `progress` entry lived here until the year retrospective shipped. It
  // promised "what you finished this year, in a sentence and then a grid of days
  // for each schedule ... no streaks - a missed day is a pale square, not a
  // broken chain", and `progress/ProgressScreen.tsx` is that promise kept.
};
